/* =====================================================================
 * DMRichCard — 리치 카드 (나우 알림, 링크 카드 등)
 * ===================================================================== */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { MessageMetadata } from '@auw/workspace-utils/types/dm';

interface Props {
  metadata: MessageMetadata;
}

const DMRichCard: React.FC<Props> = ({ metadata }) => {
  const { t } = useTranslation('common');
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);
  const cardData = metadata.card_data ?? {};
  const title = (cardData.title as string) ?? metadata.event_type ?? 'Update';
  const description = (cardData.description as string) ?? '';
  const imageUrl = cardData.image_url as string | undefined;
  const actionUrl = metadata.action_url;
  const isInviteLinkCard = metadata.event_type === 'team_invite_link_created';
  const isInternalAction = !!actionUrl && actionUrl.startsWith('/') && !actionUrl.startsWith('//');
  const provider = metadata.provider;

  const resolveActionUrlForCopy = (url: string): string => {
    if (url.startsWith('/') && typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return url;
  };

  const handleCopyInviteLink = async () => {
    if (!actionUrl || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(resolveActionUrlForCopy(actionUrl));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // 클립보드 권한/환경 이슈는 조용히 무시
    }
  };

  return (
    <Card>
      {provider && (
        <ProviderTag>
          {providerEmoji(provider)} {provider}
        </ProviderTag>
      )}
      {imageUrl && <CardImage src={imageUrl} alt="" />}
      <CardBody>
        <CardTitle>{title}</CardTitle>
        {description && <CardDesc>{description}</CardDesc>}
      </CardBody>
      {actionUrl && (isInviteLinkCard ? (
        <CardActionButton type="button" onClick={handleCopyInviteLink}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? t('dm.copied') : t('dm.copyLink')}
        </CardActionButton>
      ) : isInternalAction ? (
        <CardActionButton type="button" onClick={() => { nav(actionUrl); }}>
          <ExternalLink size={14} />
          {t('dm.open')}
        </CardActionButton>
      ) : (
        <CardActionLink href={actionUrl} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={14} />
          {t('dm.open')}
        </CardActionLink>
      ))}
    </Card>
  );
};

export default DMRichCard;

/* ---- Helpers ---- */

function providerEmoji(provider: string): string {
  const map: Record<string, string> = {
    slack: '💬',
    gmail: '📧',
    google_calendar: '📅',
    zoom: '🎥',
    notion: '📝',
    atlassian: '🔧',
    google_drive: '📁',
  };
  return map[provider] ?? '🔔';
}

/* ---- Styled ---- */

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  overflow: hidden;
  max-width: 320px;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const ProviderTag = styled.div`
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-transform: capitalize;
`;

const CardImage = styled.img`
  width: 100%;
  max-height: 160px;
  object-fit: cover;
`;

const CardBody = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CardDesc = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const cardActionStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  transition: background 0.15s;
`;

const CardActionLink = styled.a`
  ${cardActionStyle}
  color: ${({ theme }) => theme.colors.info};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const CardActionButton = styled.button`
  ${cardActionStyle}
  color: ${({ theme }) => theme.colors.info};
  background: transparent;
  border-left: none;
  border-right: none;
  border-bottom: none;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;
